import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {
    Column,
    ColumnGroup,
    useColumnOrder,
    useFlexLayout,
    usePagination,
    useSortBy,
    useTable,
} from 'react-table';

import {ReactComponent as ExportIcon} from 'assets/svg/download.svg';
import {ReactComponent as SettingsIcon} from 'assets/svg/settings.svg';
import {Icon} from 'components/ui-kit/Icon';
import {colors} from 'theme';
import {GridConfig} from 'types/api';
import {formatNumber} from 'utils/number';

import {Pagination} from '../Pagination';
import {SimplePagination} from '../Pagination/simplePagination';
import {Spinner} from '../Spinner';

import {FiltersType, mapTableFilters} from './Filters';
import {FilterContent} from './Filters/filter-content';
import {Settings} from './Setting';
import {SortingIndicator} from './sorting-indicator';
import {
    CellContainer,
    Footer,
    Header,
    HeaderCell,
    NoValuesContainer,
    NoValuesLabel,
    Root,
    SpinnerLayer,
    TableView,
    TotalRowsCount,
    HeaderActions,
    HeaderControls,
    HeaderCellTitle,
} from './styled';
import {Skeleton} from './table-skeleton';

export interface SortingItem {
    id: string;
    desc?: boolean;
}

export interface UserConfig {
    gridName: string;
    getConfig: (name: string) => Promise<GridConfig>;
    saveConfig: (config: GridConfig) => Promise<any>;
}

export type Sorting = SortingItem[];

export const isFilterActive = (filters: FiltersType, id: string) =>
    filters.reduce((prev, curr) => prev || curr.id === id, false);

export interface TableProps {
    availablePageSizes?: number[];
    className?: string;
    columns: Column<object>[];
    data?: object[];
    error?: string | null;
    hasFooter?: boolean;
    headerControls?: ReactNode;
    isLoading?: boolean;
    manualSorting?: boolean;
    onFiltersChange?: (filters: any) => void;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    onSortingChange?: (sorting: Sorting) => void;
    pageCount?: number;
    hasNext?: boolean;
    pageIndex?: number;
    sorting?: Sorting;
    filters?: FiltersType;
    totalRowsCount?: number;
    hideSetting?: boolean;
    userConfig?: UserConfig;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

const sortingDefault: Sorting = [];
const filtersDefault: FiltersType = [];

export const Table = ({
    className,
    columns,
    data,
    error,
    hasFooter = true,
    headerControls,
    isLoading,
    manualSorting = false,
    onFiltersChange,
    onPageChange,
    onSortingChange,
    pageCount: controlledPageCount,
    pageIndex: controlledPageIndex = 0,
    hasNext,
    sorting = sortingDefault,
    filters = filtersDefault,
    totalRowsCount,
    hideSetting = false,
    userConfig,
    exportConfig,
}: TableProps) => {
    const {t} = useTranslation();
    const defaultColumn = useMemo(
        () => ({
            width: 230,
        }),
        [],
    );

    const mappedColumns = useMemo(
        () =>
            onFiltersChange
                ? mapTableFilters(columns as ColumnGroup[], filters, onFiltersChange)
                : columns,
        [columns, filters, onFiltersChange],
    );

    const {
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
        setColumnOrder,
        setHiddenColumns,
        allColumns,
        rows,
        state: {sortBy, hiddenColumns},
    } = useTable(
        {
            columns: mappedColumns,
            data: data ?? [],
            initialState: {
                sortBy: sorting,
            },
            manualSortBy: manualSorting,
            disableSortRemove: true,
            manualPagination: true,
            defaultColumn,
            pageCount: controlledPageCount,
            autoResetHiddenColumns: false,
        },
        useFlexLayout,
        useSortBy,
        usePagination,
        useColumnOrder,
    );

    const isInitialMount = useRef(true);
    const [currentFilter, setCurrentFilter] = useState(null);
    const [clickPosition, setClickPosition] = useState(0);
    const [isSettingsActive, setSettingsActive] = useState(false);
    const [minHeight, setMinHeight] = useState('0px');

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            if (!onSortingChange) {
                return;
            }

            onSortingChange(sortBy);
        }
    }, [sortBy, onSortingChange]);

    useEffect(() => {
        if (userConfig) {
            userConfig.getConfig(userConfig.gridName).then((res) => {
                if (res) {
                    setColumnOrder(res.columns);
                    setHiddenColumns(res.hiddenColumns);
                }
            });
        }
    }, []);

    useEffect(() => {
        // Hide current filter popup after submit
        setCurrentFilter(null);
    }, [filters]);

    useEffect(() => {
        if (currentFilter && rows.length < 6) {
            setMinHeight('650px');
        } else {
            setMinHeight('0px');
        }
    }, [currentFilter]);

    if (error) {
        return <div>{error}</div>;
    }

    if (!data) {
        return <Skeleton />;
    }

    return (
        <Root className={className}>
            {isLoading && (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}

            {(!hideSetting || headerControls || !!exportConfig) && (
                <Header alignRight={!headerControls}>
                    <HeaderControls>{headerControls}</HeaderControls>
                    {(!hideSetting || !!exportConfig) && (
                        <HeaderActions>
                            {!hideSetting && (
                                <Icon
                                    source={SettingsIcon}
                                    width={16}
                                    fillColor={colors.brandPrimary}
                                    onClick={() => setSettingsActive(!isSettingsActive)}
                                />
                            )}
                            {exportConfig && (
                                <Icon
                                    source={ExportIcon}
                                    width={16}
                                    fillColor={
                                        exportConfig.exportPending
                                            ? colors.textMuted
                                            : colors.brandPrimary
                                    }
                                    onClick={() =>
                                        !exportConfig.exportPending && exportConfig.callback()
                                    }
                                />
                            )}
                        </HeaderActions>
                    )}
                </Header>
            )}

            <Settings
                visible={isSettingsActive}
                onClose={() => setSettingsActive(false)}
                defaultColumns={columns.map((column) => column.accessor) as string[]}
                columns={allColumns}
                setColumnOrder={setColumnOrder}
                setHiddenColumns={setHiddenColumns}
                hiddenColumns={hiddenColumns}
                userConfig={userConfig}
            />

            <TableView>
                <div
                    {...getTableProps()}
                    className="table"
                    style={{minHeight, overflowX: minHeight === '0px' ? 'auto' : 'hidden'}}
                >
                    <div className="thead">
                        <>
                            {headerGroups.map((headerGroup, index) => {
                                const isGroupHeader = index !== headerGroups.length - 1;
                                const className = isGroupHeader ? 'tr groupHeader' : 'tr';
                                return (
                                    <div
                                        {...headerGroup.getHeaderGroupProps()}
                                        className={className}
                                    >
                                        {headerGroup.headers.map((column: any) => (
                                            <HeaderCell {...column.getHeaderProps()} className="th">
                                                <HeaderCellTitle
                                                    {...column.getSortByToggleProps({
                                                        title: column.canSort
                                                            ? t('components.table.Sort')
                                                            : '',
                                                    })}
                                                >
                                                    <span style={{padding: '0px 5px'}}>
                                                        {column.render('Header')}
                                                    </span>
                                                    {!column.disableSortBy && (
                                                        <SortingIndicator
                                                            isSorted={column.isSorted}
                                                            isSortedDesc={column.isSortedDesc}
                                                        />
                                                    )}
                                                </HeaderCellTitle>
                                                {column.Filter && onFiltersChange && (
                                                    <FilterContent
                                                        clickPosition={clickPosition}
                                                        setClickPosition={setClickPosition}
                                                        currentFilter={currentFilter}
                                                        setCurrentFilter={setCurrentFilter}
                                                        column={column}
                                                        filters={filters}
                                                        isFilterActive={isFilterActive}
                                                    />
                                                )}
                                            </HeaderCell>
                                        ))}
                                    </div>
                                );
                            })}
                        </>
                    </div>
                    <div {...getTableBodyProps()} className="tbody">
                        {rows.length === 0 ? (
                            <NoValuesContainer>
                                {new Array(10).fill(0).map((_, index) => (
                                    <div key={index} className="tr">
                                        {allColumns
                                            .filter((item) => item.isVisible)
                                            .map((column) => (
                                                <div
                                                    key={column.id}
                                                    role="cell"
                                                    className="td"
                                                    style={{width: column.width}}
                                                >
                                                    <CellContainer> &nbsp;</CellContainer>
                                                </div>
                                            ))}
                                    </div>
                                ))}

                                <NoValuesLabel>
                                    {isLoading
                                        ? t('components.table.Loading') + '...'
                                        : t('components.table.NoData')}
                                </NoValuesLabel>
                            </NoValuesContainer>
                        ) : (
                            rows.map((row: any) => {
                                prepareRow(row);

                                return (
                                    <div {...row.getRowProps()} className="tr">
                                        {row.cells.map((cell: any) => (
                                            <div {...cell.getCellProps()} className="td">
                                                <CellContainer>{cell.render('Cell')}</CellContainer>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </TableView>

            {hasFooter &&
                minHeight === '0px' &&
                (totalRowsCount !== undefined ||
                    controlledPageCount !== undefined ||
                    hasNext !== undefined) && (
                    <Footer>
                        <TotalRowsCount>
                            {totalRowsCount !== undefined &&
                                `${t('components.table.Total')}: ${formatNumber(totalRowsCount)}`}
                        </TotalRowsCount>
                        {controlledPageCount !== undefined ? (
                            <Pagination
                                disabled={isLoading}
                                currentPageIndex={controlledPageIndex}
                                lastPageIndex={controlledPageCount ? controlledPageCount - 1 : 0}
                                onPageIndexChange={(pageIndex) =>
                                    onPageChange && onPageChange(pageIndex)
                                }
                            />
                        ) : (
                            hasNext !== undefined && (
                                <SimplePagination
                                    disabled={isLoading}
                                    currentPageIndex={controlledPageIndex}
                                    disableNext={!hasNext || data.length === 0}
                                    onPageIndexChange={(pageIndex) =>
                                        onPageChange && onPageChange(pageIndex)
                                    }
                                />
                            )
                        )}
                    </Footer>
                )}
        </Root>
    );
};
